/**
 * "Edited" on a note, and every version it has had (Lloyd, 23 Oct 2026: nobody may delete a note;
 * edits are fine if the changes can be seen).
 *
 * Renders nothing for a note that was never edited. The versions are loaded only when somebody asks
 * for them, so a long list of notes costs nothing extra.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Typography,
} from '@mui/material';

interface NoteVersion {
  title: string;
  desc: string;
  topic: string | null;
  at: string | null;
  by: string | null;
  current: boolean;
}

const when = (value: string | Date | null | undefined) =>
  value ? new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '';

const NoteEditHistory = (props: {
  noteGuid: string;
  editCount?: number | null;
  editedAt?: string | Date | null;
  editedBy?: string | null;
}) => {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<NoteVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!props.editCount) return null;

  const show = async () => {
    setOpen(true);
    setError(null);
    setVersions(null);
    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/user-note/history?UserNote_guid=${props.noteGuid}`,
        { credentials: 'include' },
      );
      if (!response.ok) throw new Error('Could not load the earlier versions of this note.');
      setVersions(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the earlier versions of this note.');
    }
  };

  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Edited{props.editCount > 1 ? ` ${props.editCount} times` : ''}
        {props.editedBy ? ` by ${props.editedBy}` : ''}
        {props.editedAt ? `, ${when(props.editedAt)}` : ''}
        {' · '}
        <Link component="button" type="button" variant="caption" onClick={show}>
          See earlier versions
        </Link>
      </Typography>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Note history</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error">{error}</Alert>}
          {!error && !versions && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {versions &&
            // Newest first, like the notes themselves.
            [...versions].reverse().map((version, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                {i > 0 && <Divider sx={{ mb: 2 }} />}
                <Typography variant="overline" color={version.current ? 'primary' : 'text.secondary'}>
                  {version.current ? 'Current' : i === versions.length - 1 ? 'Original' : 'Earlier version'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {[version.by || 'Staff', when(version.at)].filter(Boolean).join(' · ')}
                </Typography>
                <Typography variant="subtitle2">{version.title}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {version.desc}
                </Typography>
              </Box>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NoteEditHistory;
